import { cn } from '../../lib/utils';
import { CreditRating, LoginStatus, DocumentStatus, Priority } from '../data/mockData';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';

export interface CustomerStatusOption {
  id: number | string;
  name: string;
  display_order?: number;
  colorCode?: string;
  textColorCode?: string;
  backgroundColorCode?: string;
  subCategory?: string;
  category?: string;
}

const defaultStatusAppearance = {
  colorCode: '#6B7280',
  textColorCode: '#374151',
  backgroundColorCode: '#E5E7EB',
};

export const getCustomerStatusAppearance = (statusName?: string) => {
  const normalizedStatus = statusName?.trim().toLowerCase() || '';
  if (normalizedStatus.includes('reject') || normalizedStatus.includes('declin') || normalizedStatus.includes('cancel')) {
    return {
      colorCode: '#DC2626',
      textColorCode: '#991B1B',
      backgroundColorCode: '#FEE2E2',
    };
  }

  if (normalizedStatus.includes('pricing shared')) {
    return {
      colorCode: '#EAB308',
      textColorCode: '#854D0E',
      backgroundColorCode: '#FEF9C3',
    };
  }

  if (normalizedStatus.includes('mra shared')) {
    return {
      colorCode: '#0D9488',
      textColorCode: '#134E4A',
      backgroundColorCode: '#CCFBF1',
    };
  }

  if (normalizedStatus.includes('docs requested')) {
    return {
      colorCode: '#EA580C',
      textColorCode: '#7C2D12',
      backgroundColorCode: '#FFEDD5',
    };
  }

  if (normalizedStatus.includes('unresponsive')) {
    return {
      colorCode: '#DB2777',
      textColorCode: '#831843',
      backgroundColorCode: '#FCE7F3',
    };
  }

  if (normalizedStatus.includes('dropped by af')) {
    return {
      colorCode: '#475569',
      textColorCode: '#1E293B',
      backgroundColorCode: '#F1F5F9',
    };
  }

  if (normalizedStatus.includes('sanction') || normalizedStatus.includes('approve')) {
    return {
      colorCode: '#059669',
      textColorCode: '#065F46',
      backgroundColorCode: '#D1FAE5',
    };
  }

  if (normalizedStatus.includes('disburs')) {
    return {
      colorCode: '#2563EB',
      textColorCode: '#1D4ED8',
      backgroundColorCode: '#DBEAFE',
    };
  }

  if (normalizedStatus.includes('pending') || normalizedStatus.includes('review')) {
    return {
      colorCode: '#D97706',
      textColorCode: '#92400E',
      backgroundColorCode: '#FEF3C7',
    };
  }

  if (normalizedStatus.includes('hold')) {
    return {
      colorCode: '#7C3AED',
      textColorCode: '#5B21B6',
      backgroundColorCode: '#EDE9FE',
    };
  }

  if (normalizedStatus.includes('contact') || normalizedStatus.includes('lead')) {
    return {
      colorCode: '#0284C7',
      textColorCode: '#0C4A6E',
      backgroundColorCode: '#E0F2FE',
    };
  }

  if (normalizedStatus.includes('proposal') || normalizedStatus.includes('negotiation')) {
    return {
      colorCode: '#B45309',
      textColorCode: '#92400E',
      backgroundColorCode: '#FDE68A',
    };
  }

  if (normalizedStatus.includes('active')) {
    return {
      colorCode: '#0F766E',
      textColorCode: '#115E59',
      backgroundColorCode: '#CCFBF1',
    };
  }

  if (normalizedStatus.includes('inactive')) {
    return {
      colorCode: '#6B7280',
      textColorCode: '#4B5563',
      backgroundColorCode: '#E5E7EB',
    };
  }

  return defaultStatusAppearance;
};

export function CreditRatingBadge({ rating }: { rating: CreditRating }) {
  return (
    <span className="inline-flex h-6 w-12 items-center justify-center text-xs font-bold text-foreground">
      {rating || '—'}
    </span>
  );
}

export function PipelineStatusBadge({ status }: { status: string }) {
  const appearance = getCustomerStatusAppearance(status);

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium"
      style={{
        backgroundColor: appearance.backgroundColorCode,
        color: appearance.textColorCode,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: appearance.colorCode }}
      />
      {status}
    </span>
  );
}

const loginDot: Record<LoginStatus, string> = {
  Active: 'bg-emerald-400',
  Inactive: 'bg-slate-300',
  Pending: 'bg-amber-400',
};

export function LoginStatusBadge({ status }: { status: LoginStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn('h-1.5 w-1.5 rounded-full', loginDot[status])} />
      {status}
    </span>
  );
}

export function DocumentStatusBadge({ status, label }: { status: DocumentStatus; label?: string }) {
  const resolvedStatus = label || status || '—';
  const normalizedStatus = resolvedStatus.trim().toLowerCase();
  const dotClass = normalizedStatus.includes('sign') || normalizedStatus.includes('complete')
    ? 'bg-emerald-400'
    : normalizedStatus.includes('pending') || normalizedStatus.includes('review')
      ? 'bg-rose-400'
      : 'bg-slate-300';
  const textClass = normalizedStatus.includes('sign') || normalizedStatus.includes('complete')
    ? 'text-emerald-500'
    : normalizedStatus.includes('pending') || normalizedStatus.includes('review')
      ? 'text-rose-500'
      : 'text-slate-500';

  return (
    <span className={cn('inline-flex items-center gap-2 text-xs font-medium', textClass)}>
      <span className={cn('h-2 w-2 rounded-full', dotClass)} />
      {resolvedStatus}
    </span>
  );
}

const pipelineDotColors: Record<Exclude<Priority, '-'>, string> = {
  High: '#DC2626',
  Medium: '#D97706',
  Low: '#16A34A',
};

export function PriorityBadge({ pipeline }: { pipeline: Priority }) {
  const showPriorityDot = pipeline === 'High' || pipeline === 'Medium' || pipeline === 'Low';

  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-foreground">
      {showPriorityDot && (
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: pipelineDotColors[pipeline] }}
        />
      )}
      {pipeline}
    </span>
  );
}

export function PriorityDropdown({ value, onChange }: { value: Priority; onChange: (v: Priority) => void }) {
  const priorities: Array<Exclude<Priority, '-'>> = ['High', 'Medium', 'Low'];
  const resolvedValue = value || '-';
  const showPriorityDot = resolvedValue === 'High' || resolvedValue === 'Medium' || resolvedValue === 'Low';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'group inline-flex h-8 items-center gap-1.5 rounded-lg bg-transparent px-2 text-xs font-medium transition-colors cursor-pointer hover:bg-slate-100',
            resolvedValue === '-' ? 'text-muted-foreground' : 'text-foreground'
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {showPriorityDot && (
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: pipelineDotColors[resolvedValue as Exclude<Priority, '-'>] }}
            />
          )}
          {resolvedValue}
          <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="z-[200] min-w-[130px] rounded-xl border border-slate-200 bg-white p-1 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {priorities.map((pipeline) => (
          <DropdownMenuItem
            key={pipeline}
            onClick={() => onChange(pipeline)}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:bg-slate-50',
              resolvedValue === pipeline && 'bg-slate-50 font-semibold'
            )}
          >
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: pipelineDotColors[pipeline] }}
            />
            {pipeline}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const getMatchedStatusOption = (
  statusOptions: CustomerStatusOption[],
  statusId?: string,
  statusName?: string
) => {
  const normalizedStatusName = statusName?.trim().toLowerCase();

  return statusOptions.find((status) => {
    if (statusId && String(status.id) === String(statusId)) {
      return true;
    }

    if (normalizedStatusName && status.name.trim().toLowerCase() === normalizedStatusName) {
      return true;
    }

    return false;
  });
};

export function StatusDropdown({
  value,
  statusId,
  statusOptions = [],
  onChange,
}: {
  value: string;
  statusId?: string;
  statusOptions?: CustomerStatusOption[];
  onChange: (status: CustomerStatusOption) => void;
}) {
  const selectedStatus = getMatchedStatusOption(statusOptions, statusId, value);
  const resolvedStatusName = selectedStatus?.name || value;
  const resolvedAppearance = selectedStatus
    ? {
      colorCode: selectedStatus.colorCode || getCustomerStatusAppearance(resolvedStatusName).colorCode,
      textColorCode: selectedStatus.textColorCode || getCustomerStatusAppearance(resolvedStatusName).textColorCode,
      backgroundColorCode:
        selectedStatus.backgroundColorCode || getCustomerStatusAppearance(resolvedStatusName).backgroundColorCode,
    }
    : getCustomerStatusAppearance(resolvedStatusName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="group inline-flex h-8 items-center gap-1.5 rounded-lg bg-transparent px-2 text-xs font-medium text-foreground transition-colors cursor-pointer hover:bg-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
            style={{
              backgroundColor: resolvedAppearance.backgroundColorCode,
              color: resolvedAppearance.textColorCode,
            }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: resolvedAppearance.colorCode }}
            />
            {resolvedStatusName}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="z-[200] min-w-[160px] rounded-xl border border-slate-200 bg-white p-1 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {statusOptions.length === 0 && (
          <DropdownMenuItem disabled className="py-2 text-xs text-muted-foreground">
            No status available
          </DropdownMenuItem>
        )}
        {statusOptions.map((status) => {
          const appearance = {
            colorCode: status.colorCode || getCustomerStatusAppearance(status.name).colorCode,
            textColorCode: status.textColorCode || getCustomerStatusAppearance(status.name).textColorCode,
            backgroundColorCode:
              status.backgroundColorCode || getCustomerStatusAppearance(status.name).backgroundColorCode,
          };
          const isSelected = selectedStatus && String(selectedStatus.id) === String(status.id);

          return (
            <DropdownMenuItem
              key={status.id}
              onClick={() => onChange(status)}
              className={cn(
                'cursor-pointer rounded-lg px-2 py-1.5 hover:bg-slate-50 focus:bg-slate-50',
                isSelected && 'bg-slate-50'
              )}
            >
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                style={{
                  backgroundColor: appearance.backgroundColorCode,
                  color: appearance.textColorCode,
                }}
              >
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: appearance.colorCode }}
                />
                {status.name}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface ProductsCellProps {
  products: string[];
}

export function ProductsCell({ products }: ProductsCellProps) {
  if (products?.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  const [firstProduct, ...remainingProducts] = products;

  return (
    <div className="flex items-center gap-2">
      <span className="max-w-[150px] truncate text-sm text-foreground">{firstProduct}</span>
      {remainingProducts.length > 0 && (
        <span className="inline-flex h-7 min-w-[28px] items-center justify-center rounded-full bg-[#eef7f8] px-2 text-xs font-semibold text-[#5da8b4]">
          +{remainingProducts.length}
        </span>
      )}
    </div>
  );
}
